import { FormDisplayTableRow, Text, TextField } from '@shared/components';
import { InlineTextField } from '@shared/components/inline-text-field/InlineTextField';
import useMovementDetails from '@shared/graphql/hooks/movements/useMovementDetails';
import { MovementDetails, MovementEquipment } from '@shared/types/movement';
import { getWeightUoMOptions } from '@shared/utils/forms/weight';
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CoverageStatusChip } from './CoverageStatusChip';
import {
  formatMovementEquipmentTypeLabel,
  parseEquipmentControlMode,
} from './formatMovementEquipmentTypeLabel';
import { StopPreviews } from './StopPreviews';

export function MovementDetailsDisplay({
  movementId,
  hidePricing,
  commodity,
  showCoverageStatus,
}: {
  movementId: string;
  hidePricing?: boolean;
  commodity?: string;
  showCoverageStatus?: boolean;
}) {
  const { movementDetails, isLoading } = useMovementDetails({
    id: movementId,
    pollInterval: 5000,
  });

  const methods = useForm<MovementDetails>({
    defaultValues: {} as MovementDetails,
    mode: 'onChange',
  });

  const prevPayloadRef = useRef<MovementDetails | null>(null);
  const { reset } = methods;

  useEffect(() => {
    if (!movementDetails) return;

    if (!isEqual(prevPayloadRef.current, movementDetails)) {
      reset(movementDetails, { keepDirty: false });
      prevPayloadRef.current = movementDetails;
    }
  }, [movementDetails, reset]);

  if (isLoading) return <span>Loading…</span>;
  if (!movementDetails) return <span>No shipment data available.</span>;

  const { coverageStatus, stops } = movementDetails;
  const showStopPreviews = stops.length > 0;
  const maxPriceName = 'pricingData.maxPrice';
  const targetPriceName = 'pricingData.targetPrice';
  const startingPriceName = 'pricingData.startingPrice';

  const save = (_: string) => {
    _;
  };

  const weightUoMOptions = getWeightUoMOptions();

  return (
    <div className="flex flex-col gap-2">
      <FormProvider {...methods}>
        {showCoverageStatus && (
          <div className="px-4 pb-2">
            <FormDisplayTableRow
              displayText="Coverage status"
              value={coverageStatus ? undefined : 'Not specified'}
              statusChip={
                coverageStatus ? (
                  <CoverageStatusChip status={coverageStatus} />
                ) : undefined
              }
            />
          </div>
        )}
        {showStopPreviews && <StopPreviews stops={stops} />}
        <div className="mb-2 flex flex-col gap-[2px] px-[16px]">
          {movementDetails.equipment && (
            <div className="mb-2 flex flex-col">
              <Text type="body-sm" className="mb-2 text-gray-500">
                Equipment Options
              </Text>
              {movementDetails.equipment.map((equipment, i) => (
                <EquipmentRow key={i} equipment={equipment} />
              ))}
            </div>
          )}
          <div className="mb-2 flex items-center gap-2">
            <Text
              type="body-sm"
              className="mr-4 whitespace-nowrap text-gray-500"
            >
              Commodity
            </Text>
            <TextField
              isDisabled
              transparentWithoutFocus
              value={commodity ?? ''}
              size="medium"
              placeholder="Not specified"
              aria-label="Commodity"
              className="text-black"
            />
          </div>
          <DimensionDisplayField
            title="Weight"
            value={movementDetails.weight?.value}
            unit={movementDetails.weight?.unit}
            UoMOptions={weightUoMOptions}
          />
        </div>
        {!hidePricing && (
          <>
            <Text type="body-sm" className="text-gray-700" isHeavy>
              Pricing
            </Text>
            <div className="flex flex-col gap-[2px] p-[16px] pt-[8px]">
              <InlineTextField<MovementDetails>
                label="Max"
                name={maxPriceName}
                required={true}
                save={save}
                isDisabled={true}
                placeholder="Not specified"
              />
              {movementDetails.pricingData?.targetPrice && (
                <InlineTextField<MovementDetails>
                  label="Target"
                  name={targetPriceName}
                  required={true}
                  save={save}
                  isDisabled={true}
                  placeholder="Not specified"
                />
              )}
              {movementDetails.pricingData?.startingPrice && (
                <InlineTextField<MovementDetails>
                  label="Starting"
                  name={startingPriceName}
                  required={true}
                  save={save}
                  isDisabled={true}
                  placeholder="Not specified"
                />
              )}
            </div>
          </>
        )}
      </FormProvider>
    </div>
  );
}

interface DimensionDisplayFieldProps {
  title: string;
  value: number | string | undefined;
  unit: string | undefined;
  UoMOptions: { id: string; label: string }[];
}

export function DimensionDisplayField({
  title,
  value,
  unit,
  UoMOptions,
}: DimensionDisplayFieldProps) {
  const selectedUoMValue = UoMOptions.find((o) => o.id === unit)?.label || unit;
  const displayValue =
    value !== undefined && selectedUoMValue
      ? `${value} ${selectedUoMValue}`
      : value !== undefined
      ? String(value)
      : '';

  return (
    <div className="flex items-center gap-2">
      {title ? (
        <Text type="body-sm" className="mr-4 whitespace-nowrap text-gray-500">
          {title}
        </Text>
      ) : null}
      <TextField
        isDisabled
        transparentWithoutFocus
        value={displayValue}
        size="medium"
        placeholder="Not specified"
        aria-label={title ? `${title} Value` : 'Equipment length'}
        className="text-black"
      />
    </div>
  );
}

function EquipmentRow({ equipment }: { equipment: MovementEquipment }) {
  const lengthUoMOptions = [
    { id: 'FT', label: 'ft' },
    { id: 'IN', label: 'in' },
    { id: 'CM', label: 'cm' },
  ];
  const temperatureLabel = formatEquipmentTemperature(equipment.temperature);
  const controlMode = parseEquipmentControlMode(equipment.description);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-row">
        <TextField
          isDisabled
          transparentWithoutFocus
          value={formatMovementEquipmentTypeLabel(equipment)}
          size="medium"
          placeholder="Not specified"
        />
        <DimensionDisplayField
          title=""
          value={equipment.equipmentLength?.value}
          unit={equipment.equipmentLength?.units}
          UoMOptions={lengthUoMOptions}
        />
      </div>
      {temperatureLabel && (
        <div className="flex items-center gap-2">
          <Text type="body-sm" className="mr-4 whitespace-nowrap text-gray-500">
            Temperature
          </Text>
          <TextField
            isDisabled
            transparentWithoutFocus
            value={temperatureLabel}
            size="medium"
            placeholder="Not specified"
            aria-label="Temperature"
            className="text-black"
          />
        </div>
      )}
      {controlMode && (
        <div className="flex items-center gap-2">
          <Text type="body-sm" className="mr-4 whitespace-nowrap text-gray-500">
            Control mode
          </Text>
          <TextField
            isDisabled
            transparentWithoutFocus
            value={controlMode}
            size="medium"
            placeholder="Not specified"
            aria-label="Control mode"
            className="text-black"
          />
        </div>
      )}
    </div>
  );
}

function formatEquipmentTemperature(
  temperature?: MovementEquipment['temperature']
): string | undefined {
  if (!temperature) return undefined;
  const { min, max, units } = temperature;
  if (min == null && max == null) return undefined;
  const unit = units ?? 'F';
  if (min != null && max != null && min !== max) {
    return `${min} to ${max} ${unit}`;
  }
  return `${min ?? max} ${unit}`;
}
